import React, { useEffect, useState } from "react";
import { staffQueue } from "../../api";
import { LoadingSpinner, PageHeader } from "../../components/ui";
import { useAuth } from "../../context/AuthContext";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from "recharts";

const TABS = ["Today", "Weekly", "Monthly", "Compare"];

export default function StaffStatsPage() {
  const { user }  = useAuth();
  const [tab,     setTab]    = useState(0);
  const [today,   setToday]  = useState(null);
  const [weekly,  setWeekly] = useState(null);
  const [monthly, setMonthly]= useState(null);
  const [compare, setCompare]= useState(null);
  const [comparePeriod, setComparePeriod] = useState("week");
  const [ranking, setRanking]= useState(null);
  const [rating,  setRating] = useState(null);
  const [loading, setLoading]= useState(true);

  useEffect(() => {
    Promise.all([
      staffQueue.todayStats(),
      staffQueue.myRanking(),
      staffQueue.myRating(),
    ]).then(([t, r, rt]) => {
      setToday(t.data);
      setRanking(r.data);
      setRating(rt.data);
    }).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (tab === 1 && !weekly) {
      staffQueue.weeklyStats().then(r => setWeekly(r.data)).catch(() => {});
    }
    if (tab === 2 && !monthly) {
      staffQueue.monthlyStats().then(r => setMonthly(r.data)).catch(() => {});
    }
  }, [tab]);

  useEffect(() => {
    staffQueue.compareStats(comparePeriod).then(r => setCompare(r.data)).catch(() => {});
  }, [comparePeriod]);

  const coachingMsg = (stats) => {
    if (!stats) return null;
    const s = stats.served_today ?? stats.total_served ?? stats.served ?? 0;
    const e = stats.efficiency ?? stats.week_efficiency ?? stats.month_efficiency ?? 0;
    if (e >= 90) return { icon:"🔥", title:"Outstanding performance!", sub:"You're in the top tier. Keep it up." };
    if (e >= 70) return { icon:"💪", title:"Great work today.", sub:"Strong efficiency — aim to maintain this pace." };
    if (s >= 1)  return { icon:"👍", title:"Good start.", sub:"Keep serving — your score improves with volume." };
    return { icon:"🟡", title:"Ready to start?", sub:"Call your first patient to begin tracking." };
  };

  return (
    <div style={S.root}>
      <PageHeader title="My Performance" subtitle={`${user?.name} — personal performance dashboard`} />

      <div style={S.tabs}>
        {TABS.map((t, i) => (
          <button key={t} onClick={() => setTab(i)} style={{ ...S.tab, ...(tab === i ? S.tabActive : {}) }}>{t}</button>
        ))}
      </div>

      {loading ? <LoadingSpinner /> : (
        <div style={S.panel}>
          {tab === 0 && <TodayTab today={today} ranking={ranking} rating={rating} coach={coachingMsg(today)} />}
          {tab === 1 && <WeeklyTab weekly={weekly} coach={coachingMsg(weekly)} />}
          {tab === 2 && <MonthlyTab monthly={monthly} coach={coachingMsg(monthly)} />}
          {tab === 3 && <CompareTab compare={compare} period={comparePeriod} setPeriod={setComparePeriod} />}
        </div>
      )}
    </div>
  );
}

// ── Today Tab ─────────────────────────────────────────────────────────────────
function TodayTab({ today, ranking, rating, coach }) {
  return (
    <div style={S.grid}>
      <div style={S.statsRow}>
        <StatCard label="Served Today"     value={today?.served_today ?? 0}          color="#22C55E" />
        <StatCard label="Avg Service (min)" value={today?.avg_service_minutes ?? "—"} color="#8CBCE0" />
        <StatCard label="Efficiency Score" value={today?.efficiency != null ? `${today.efficiency}%` : "—"} color="#A78BFA" />
        <StatCard label="Patient Rating" value={rating?.count ? `⭐ ${rating.avg_stars}` : "—"} color="#EAB308" />
      </div>
      {rating?.count > 0 && (
        <div style={{ ...S.infoCard, borderColor: "rgba(234,179,8,0.2)", background: "rgba(234,179,8,0.05)" }}>
          <span style={{ fontSize: "16px" }}>⭐</span>
          <div>
            <div style={{ fontSize: "14px", fontWeight: "600", color: "#EAB308" }}>
              {rating.avg_stars} average from {rating.count} rating{rating.count !== 1 ? "s" : ""}
            </div>
            {rating.recent?.find(r => r.comment) && (
              <div style={{ fontSize: "12px", color: "var(--text-secondary)", marginTop: "2px", fontStyle: "italic" }}>
                "{rating.recent.find(r => r.comment).comment}"
              </div>
            )}
          </div>
        </div>
      )}
      {ranking?.rank && (
        <div style={S.rankCard}>
          <div style={{ fontSize:"24px", marginBottom:"6px" }}>🏅</div>
          <div style={{ fontSize:"20px", fontWeight:"700", color:"var(--text-primary)" }}>
            Ranked #{ranking.rank} of {ranking.total_staff}
          </div>
          <div style={{ fontSize:"13px", color:"var(--text-secondary)", marginTop:"4px" }}>
            {ranking.scope === "station" ? "Among staff at your station today" : "Among staff at your facility today"}
          </div>
          <div style={{ marginTop:"10px", background:"var(--surface-border)", borderRadius:"8px", padding:"6px 14px", display:"inline-block" }}>
            <span style={{ fontSize:"14px", color:"#8CBCE0", fontWeight:"600" }}>Efficiency: {ranking.my_score}%</span>
          </div>
        </div>
      )}
      {coach && <CoachCard {...coach} />}
    </div>
  );
}

// ── Weekly Tab ────────────────────────────────────────────────────────────────
function WeeklyTab({ weekly, coach }) {
  if (!weekly) return <LoadingSpinner />;
  const bestDay = weekly.best_day;
  return (
    <div style={S.grid}>
      <div style={S.statsRow}>
        <StatCard label="This Week Served"  value={weekly.total_served}    color="#22C55E" />
        <StatCard label="Avg Service (min)" value={weekly.avg_service}     color="#8CBCE0" />
        <StatCard label="Week Efficiency"   value={`${weekly.week_efficiency}%`} color="#A78BFA" />
      </div>
      {bestDay && bestDay.served > 0 && (
        <div style={{ ...S.infoCard, borderColor:"rgba(234,179,8,0.2)", background:"rgba(234,179,8,0.05)" }}>
          <span style={{ fontSize:"16px" }}>⭐</span>
          <div>
            <div style={{ fontSize:"14px", fontWeight:"600", color:"#EAB308" }}>Best Day: {bestDay.day}</div>
            <div style={{ fontSize:"12px", color:"var(--text-secondary)" }}>{bestDay.served} patients served, {bestDay.avg_service} min avg</div>
          </div>
        </div>
      )}
      <div style={S.chartSection}>
        <div style={S.chartTitle}>Daily Volume (this week)</div>
        <ResponsiveContainer width="100%" height={180}>
          <BarChart data={weekly.days} barSize={28}>
            <XAxis dataKey="day" tick={{ fontSize:11, fill:"var(--text-secondary)" }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fontSize:11, fill:"var(--text-secondary)" }} axisLine={false} tickLine={false} allowDecimals={false} />
            <Tooltip contentStyle={{ background:"var(--table-header-bg)", border:"1px solid rgba(64,141,201,0.2)", borderRadius:"8px", fontSize:"12px", color:"var(--text-primary)" }} cursor={{ fill:"rgba(64,141,201,0.05)" }} />
            <Bar dataKey="served" radius={[4,4,0,0]}>
              {weekly.days.map((d, i) => (
                <Cell key={i} fill={d.is_today ? "#408DC9" : d.served === weekly.best_day?.served && d.served > 0 ? "#22C55E" : "#1E3A5F"} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
        <div style={{ display:"flex", gap:"16px", marginTop:"8px" }}>
          <LegendDot color="#408DC9" label="Today" />
          <LegendDot color="#22C55E" label="Best day" />
          <LegendDot color="#1E3A5F" label="Other days" />
        </div>
      </div>
      {coach && <CoachCard {...coach} />}
    </div>
  );
}

// ── Monthly Tab ───────────────────────────────────────────────────────────────
function MonthlyTab({ monthly, coach }) {
  if (!monthly) return <LoadingSpinner />;
  return (
    <div style={S.grid}>
      <div style={{ fontSize:"13px", color:"var(--text-secondary)", marginBottom:"4px" }}>{monthly.month}</div>
      <div style={S.statsRow}>
        <StatCard label="Month Served"     value={monthly.total_served}         color="#22C55E" />
        <StatCard label="Avg Service (min)" value={monthly.avg_service}         color="#8CBCE0" />
        <StatCard label="Month Efficiency" value={`${monthly.month_efficiency}%`} color="#A78BFA" />
      </div>
      <div style={S.chartSection}>
        <div style={S.chartTitle}>Weekly Breakdown</div>
        <ResponsiveContainer width="100%" height={160}>
          <BarChart data={monthly.weeks} barSize={32}>
            <XAxis dataKey="week" tick={{ fontSize:11, fill:"var(--text-secondary)" }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fontSize:11, fill:"var(--text-secondary)" }} axisLine={false} tickLine={false} allowDecimals={false} />
            <Tooltip contentStyle={{ background:"var(--table-header-bg)", border:"1px solid rgba(64,141,201,0.2)", borderRadius:"8px", fontSize:"12px", color:"var(--text-primary)" }} cursor={{ fill:"rgba(64,141,201,0.05)" }} />
            <Bar dataKey="served" fill="#408DC9" radius={[4,4,0,0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
      <div style={S.weekTable}>
        {monthly.weeks.map((w, i) => (
          <div key={i} style={S.weekRow}>
            <span style={{ fontSize:"13px", color:"var(--text-secondary)", fontWeight:"600" }}>{w.week}</span>
            <span style={{ fontSize:"13px", color:"var(--text-primary)" }}>{w.served} served</span>
            <span style={{ fontSize:"12px", color:"var(--text-secondary)" }}>{w.avg_service} min avg</span>
            <span style={{ fontSize:"12px", color:"#A78BFA" }}>{w.efficiency}% eff</span>
          </div>
        ))}
      </div>
      {coach && <CoachCard {...coach} />}
    </div>
  );
}

// ── Compare Tab ───────────────────────────────────────────────────────────────
function CompareTab({ compare, period, setPeriod }) {
  return (
    <div style={S.grid}>
      <div style={{ display:"flex", gap:"8px" }}>
        {["week", "month"].map(p => (
          <button key={p} onClick={() => setPeriod(p)} style={{ ...S.periodBtn, ...(period === p ? S.periodBtnActive : {}) }}>
            {p === "week" ? "Week vs Week" : "Month vs Month"}
          </button>
        ))}
      </div>
      {!compare ? <LoadingSpinner /> : (
        <>
          <div style={S.compareGrid}>
            <CompareCard label="Patients Served" current={compare.current.served} previous={compare.previous.served} change={compare.changes.served} currentLabel={compare.current.label} previousLabel={compare.previous.label} color="#22C55E" />
            <CompareCard label="Avg Service Time" current={`${compare.current.avg_service}m`} previous={`${compare.previous.avg_service}m`} change={compare.changes.avg_service ? -compare.changes.avg_service : null} currentLabel={compare.current.label} previousLabel={compare.previous.label} color="#8CBCE0" invertChange />
            <CompareCard label="Efficiency Score" current={`${compare.current.efficiency}%`} previous={`${compare.previous.efficiency}%`} change={compare.changes.efficiency} currentLabel={compare.current.label} previousLabel={compare.previous.label} color="#A78BFA" />
          </div>
          <CompareCoachMsg changes={compare.changes} />
        </>
      )}
    </div>
  );
}

function CompareCard({ label, current, previous, change, currentLabel, previousLabel, color, invertChange }) {
  const isPositive = change !== null && (invertChange ? change < 0 : change > 0);
  const isNegative = change !== null && (invertChange ? change > 0 : change < 0);
  return (
    <div style={S.compareCard}>
      <div style={{ fontSize:"11px", color:"var(--text-secondary)", fontWeight:"600", letterSpacing:"0.06em", textTransform:"uppercase", marginBottom:"12px" }}>{label}</div>
      <div style={{ display:"flex", gap:"8px", alignItems:"flex-end", marginBottom:"8px" }}>
        <div style={{ fontSize:"28px", fontWeight:"800", color, letterSpacing:"-0.5px" }}>{current}</div>
        {change !== null && (
          <div style={{ fontSize:"13px", fontWeight:"600", color: isPositive ? "#22C55E" : isNegative ? "#EF4444" : "var(--text-secondary)", marginBottom:"4px" }}>
            {isPositive ? "▲" : isNegative ? "▼" : "—"} {change !== null ? `${Math.abs(change)}%` : ""}
          </div>
        )}
      </div>
      <div style={{ fontSize:"11px", color:"var(--text-secondary)" }}>{currentLabel}</div>
      <div style={{ borderTop:"1px solid rgba(64,141,201,0.08)", marginTop:"10px", paddingTop:"10px" }}>
        <div style={{ fontSize:"16px", fontWeight:"600", color:"var(--text-muted)" }}>{previous}</div>
        <div style={{ fontSize:"11px", color:"var(--text-faint)" }}>{previousLabel}</div>
      </div>
    </div>
  );
}

function CompareCoachMsg({ changes }) {
  const servChange = changes?.served;
  const effChange  = changes?.efficiency;
  let icon = "📊"; let title = "Keep tracking."; let sub = "Compare periods regularly to spot trends.";
  if (effChange > 10)  { icon = "🔥"; title = "Significant improvement!"; sub = `Efficiency up ${effChange}% from last period. Excellent work.`; }
  else if (effChange > 0) { icon = "📈"; title = "Trending up."; sub = `Small but positive improvement. Keep the momentum.`; }
  else if (effChange < -10) { icon = "⚠️"; title = "Decline detected."; sub = "Efficiency dropped vs last period. Review your pace."; }
  else if (effChange < 0) { icon = "📉"; title = "Slight dip."; sub = "Minor decline — focus on consistent service time."; }
  return <CoachCard icon={icon} title={title} sub={sub} />;
}

// ── Shared Components ─────────────────────────────────────────────────────────
function StatCard({ label, value, color }) {
  return (
    <div style={S.statCard}>
      <div style={{ fontSize:"12px", color:"var(--text-secondary)", fontWeight:"600", letterSpacing:"0.05em", textTransform:"uppercase", marginBottom:"6px" }}>{label}</div>
      <div style={{ fontSize:"32px", fontWeight:"800", color, letterSpacing:"-1px", lineHeight:1 }}>{value}</div>
    </div>
  );
}

function CoachCard({ icon, title, sub }) {
  return (
    <div style={S.coachCard}>
      <span style={{ fontSize:"28px" }}>{icon}</span>
      <div>
        <div style={{ fontSize:"16px", fontWeight:"600", color:"var(--text-primary)", marginBottom:"5px" }}>{title}</div>
        <div style={{ fontSize:"13px", color:"var(--text-secondary)", lineHeight:"1.5" }}>{sub}</div>
      </div>
    </div>
  );
}

function LegendDot({ color, label }) {
  return (
    <div style={{ display:"flex", alignItems:"center", gap:"6px" }}>
      <div style={{ width:"10px", height:"10px", borderRadius:"2px", background:color, flexShrink:0 }} />
      <span style={{ fontSize:"11px", color:"var(--text-secondary)" }}>{label}</span>
    </div>
  );
}

// ── Styles ────────────────────────────────────────────────────────────────────
const S = {
  root:        { padding:"32px", fontFamily:"'DM Sans','Segoe UI',sans-serif", maxWidth:"860px" },
  tabs:        { display:"flex", gap:"4px", borderBottom:"1px solid var(--surface-border)", marginBottom:"24px" },
  tab:         { background:"none", border:"none", borderBottom:"2px solid transparent", padding:"10px 16px", fontSize:"13px", fontWeight:"500", color:"var(--text-secondary)", cursor:"pointer", fontFamily:"inherit", marginBottom:"-1px" },
  tabActive:   { color:"#8CBCE0", borderBottomColor:"#408DC9" },
  panel:       {},
  grid:        { display:"flex", flexDirection:"column", gap:"16px" },
  statsRow:    { display:"flex", gap:"12px", flexWrap:"wrap" },
  statCard:    { flex:1, minWidth:"140px", background:"var(--surface-bg)", border:"1px solid var(--surface-border)", borderRadius:"12px", padding:"16px 18px" },
  rankCard:    { background:"var(--surface-bg)", border:"1px solid rgba(64,141,201,0.2)", borderRadius:"14px", padding:"20px 24px", textAlign:"center" },
  infoCard:    { display:"flex", alignItems:"center", gap:"12px", background:"var(--surface-bg)", border:"1px solid var(--surface-border)", borderRadius:"12px", padding:"14px 18px" },
  coachCard:   { display:"flex", gap:"16px", alignItems:"flex-start", background:"var(--surface-bg)", border:"1px solid var(--surface-border)", borderRadius:"14px", padding:"20px 22px" },
  chartSection:{ background:"var(--surface-bg)", border:"1px solid var(--surface-border)", borderRadius:"12px", padding:"16px 18px" },
  chartTitle:  { fontSize:"12px", fontWeight:"700", color:"var(--text-muted)", letterSpacing:"0.06em", textTransform:"uppercase", marginBottom:"12px" },
  weekTable:   { display:"flex", flexDirection:"column", gap:"8px" },
  weekRow:     { display:"flex", justifyContent:"space-between", alignItems:"center", padding:"10px 14px", background:"var(--surface-bg)", border:"1px solid rgba(64,141,201,0.08)", borderRadius:"8px" },
  compareGrid: { display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:"12px" },
  compareCard: { background:"var(--surface-bg)", border:"1px solid var(--surface-border)", borderRadius:"12px", padding:"16px 18px" },
  periodBtn:   { padding:"7px 16px", borderRadius:"8px", border:"1px solid rgba(64,141,201,0.2)", background:"transparent", color:"var(--text-secondary)", fontSize:"13px", fontWeight:"500", cursor:"pointer", fontFamily:"inherit" },
  periodBtnActive:{ background:"var(--surface-border)", color:"#8CBCE0", borderColor:"rgba(64,141,201,0.4)" },
};
